import { useState } from 'react';
import { EntryImage, getDisplayUrl } from '../../hooks/useImages';

interface ImageThumbnailProps {
  image: EntryImage;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'w-12 h-12',
  md: 'w-20 h-20',
  lg: 'w-24 h-24',
};

export function ImageThumbnail({ image, onClick, size = 'md' }: ImageThumbnailProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const imageUrl = getDisplayUrl(image);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${sizeClasses[size]} rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 relative focus:outline-none focus:ring-2 focus:ring-blue-500`}
    >
      {!error ? (
        <>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            </div>
          )}
          <img
            src={imageUrl}
            alt={image.filename}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            className={`w-full h-full object-cover transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
          />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
    </button>
  );
}

interface ImageGridProps {
  images: EntryImage[];
  onImageClick?: (index: number) => void;
  columns?: 2 | 3;
  size?: 'sm' | 'md' | 'lg';
}

export function ImageGrid({ images, onImageClick, size = 'sm' }: ImageGridProps) {
  if (images.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {images.map((image, index) => (
        <ImageThumbnail
          key={image.id}
          image={image}
          size={size}
          onClick={() => onImageClick?.(index)}
        />
      ))}
    </div>
  );
}
